import React from "react";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { SettingsProvider } from "@/lib/settings";

import { LoginPage, ForgotPasswordPage, ResetPasswordPage, SetPasswordPage } from "@/pages/AuthPages";
import AdminLayout from "@/layouts/AdminLayout";
import BrandLayout from "@/layouts/BrandLayout";
import CampaignsListPage from "@/pages/admin/CampaignsListPage";
import BrandsPage from "@/pages/admin/BrandsPage";
import UsersPage from "@/pages/admin/UsersPage";
import WhitelistPage from "@/pages/admin/WhitelistPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import EmailManagementPage from "@/pages/admin/EmailManagementPage";
import ConfiguratorPage from "@/pages/admin/ConfiguratorPage";
import BrandCampaignsPage from "@/pages/brand/BrandCampaignsPage";
import TrackingPage from "@/pages/brand/TrackingPage";

import "@/index.css";

function RequireAuth({ role }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--text-secondary)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.type !== role) return <Navigate to={user.type === "admin" ? "/admin/campaigns" : "/campaigns"} replace />;
  return <Outlet />;
}

function AnyAuth() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm text-[var(--text-secondary)]">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.type === "admin" ? "/admin/campaigns" : "/campaigns"} replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <Toaster position="top-right" richColors closeButton />
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route path="/set-password/:token" element={<SetPasswordPage />} />

          {/* Admin */}
          <Route element={<RequireAuth role="admin" />}>
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Navigate to="/admin/campaigns" replace />} />
              <Route path="campaigns" element={<CampaignsListPage />} />
              <Route path="campaigns/:id/config" element={<ConfiguratorPage />} />
              <Route path="brands" element={<BrandsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="whitelist" element={<WhitelistPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="emails" element={<EmailManagementPage />} />
            </Route>
          </Route>

          {/* Brand */}
          <Route element={<RequireAuth role="brand" />}>
            <Route element={<BrandLayout />}>
              <Route path="/campaigns" element={<BrandCampaignsPage />} />
            </Route>
          </Route>

          {/* Tracking — accessible by both admin (preview) and brand (gated) */}
          <Route element={<AnyAuth />}>
            <Route path="/campaigns/:id/tracking" element={<TrackingPage />} />
          </Route>

          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}
